/* eslint-disable no-param-reassign */
/**
 * Pricing slider component.
 *
 * @param {HTMLElement} sliderWrapper
 */
export function PricingSlider(sliderWrapper) {
  const recPlans = document.querySelector('#recommended-plans');
  const otherPlans = document.querySelector('#other-plans');
  const sliderInput = sliderWrapper.querySelector('#pricing-slider');
  const storeGeoCheckbox = sliderWrapper.querySelector('#pricing-checkbox');
  const sliderValueThumb = sliderWrapper.querySelector('.pricing-slider__slider-value-thumb');
  const sliderValueSide = sliderWrapper.querySelector('#selected-txn-cnt');
  let storeGeocode = false;
  let prevCardGroup = 0;

  let range = JSON.parse(sliderWrapper.dataset?.range).sort((a, b) => a - b);
  range = range ?? [0, 15000, 30000, 75000, 200000, 500000, 750000];
  const maxTxnNum = Math.max(...range);

  // vars for slider piecewise function
  const segmentLength = 100 / (range.length - 1);
  const slopes = [];
  const intercepts = [];

  // Calculate slopes and intercepts
  for (let i = 0; i < range.length - 1; i++) {
    const slope = (range[i + 1] - range[i]) / segmentLength;
    slopes.push(slope);

    const intercept = range[i] - slope * (i * segmentLength);
    intercepts.push(intercept);
  }

  /**
   * round float to nearest hundred
   *
   * @param {float} val
   */
  const roundToNearestHundred = (val) => Math.round(val / 100) * 100;

  /**
   * convert slider value (0.0 - 100.0) to appropriate transaction numbers using piecewise function
   *
   * @param {float} val
   */
  const percentToTxnNum = (val) => {
    // Determine the index (segment) of the current slider value
    let idx = Math.floor(val / segmentLength);
    idx = idx >= slopes.length ? slopes.length - 1 : idx;

    const mx = slopes[idx] * val;
    const b = intercepts[idx];
    let txnNum = roundToNearestHundred(mx + b);

    txnNum = txnNum > maxTxnNum ? maxTxnNum : txnNum;

    return txnNum;
  };

  /**
   * update visible pricing cards based on selected transaction numbers
   *
   * @param {int} value
   */
  const updatePricingCards = (value, forceUpdate = false) => {
    let newCardGroup = range.find((num) => num >= value);
    newCardGroup = newCardGroup > 0 ? newCardGroup : range[1];

    if (prevCardGroup !== newCardGroup || forceUpdate) {
      const txnGroupClass = `txn-${newCardGroup}`;

      let cards = Array.from(document.querySelectorAll('.pricing-card'));
      cards = cards.sort((a, b) => a.dataset.price - b.dataset.price);

      cards.forEach((card) => {
        const containsClass = card.classList.contains(txnGroupClass);

        if (containsClass && !storeGeocode) {
          recPlans.appendChild(card);
        } else if (containsClass && storeGeocode && card.dataset.erg) {
          recPlans.appendChild(card);
        } else {
          otherPlans.appendChild(card);
        }
      });

      // if no cards are visible, show the closest card above the selected txn threshold
      if (recPlans.children.length === 0) {
        const isErg = storeGeocode ? "[data-erg='1']" : '';
        const allCards = Array.from(document.querySelectorAll(`.pricing-card${isErg}`));
        const filteredErgCards = allCards.filter((card) => card.dataset.txn > newCardGroup);
        const card = filteredErgCards[0];

        if (card) recPlans.appendChild(card);
        else {
          const enterpriseCard = document.querySelector('.pricing-card:has(#enterprise-flag)');
          if (enterpriseCard) recPlans.appendChild(enterpriseCard);
        }
      }

      prevCardGroup = newCardGroup;
    }
  };

  /**
   * update card visibility based on checkbox value
   *
   * @param {float} checkboxVal
   */
  const updateStoreGeocode = (checkboxVal) => {
    storeGeocode = checkboxVal;

    updatePricingCards(prevCardGroup, true);
  };

  /**
   * update elements with new slider value
   *
   * @param {float} percent
   */
  const updateProgress = (percent) => {
    const value = percentToTxnNum(percent);
    let valueStr = value.toLocaleString();
    valueStr = value >= maxTxnNum ? `${valueStr}+` : valueStr;

    sliderValueThumb.textContent = valueStr;
    sliderValueSide.textContent = valueStr;
    sliderWrapper.style.setProperty('--progress', `${(percent * 97) / 100}%`);

    updatePricingCards(value);
    sliderInput.ariaValueText = `${Math.round(value)} transactions`;
  };

  /**
   * equally spaces out the postions of the transaction markers
   */
  const setMarkerPositions = () => {
    const txnMarkers = sliderWrapper.querySelectorAll('#tx-markers span');

    txnMarkers.forEach((marker, i) => {
      let percent = (i / (range.length - 1)) * 100;
      percent = percent < 100 ? percent : 100;
      const markerWidth = marker.offsetWidth / 4;

      marker.style.left = `calc(${(percent * 0.975)}% - ${markerWidth}px)`;
      marker.value = percent;
    });
  };

  /**
   * Initialize our component.
   */
  const init = () => {
    let percent = parseFloat(sliderInput.value, 10);
    updateProgress(percent);
    setMarkerPositions();

    storeGeoCheckbox.addEventListener('input', (e) => {
      updateStoreGeocode(e.target.checked);
    });

    sliderInput.addEventListener('input', (e) => {
      percent = parseFloat(e.target.value, 10);

      updateProgress(percent);
    });
  };
  init();
}
